/**
 * Epic AB#1824 — Matrix-local types + helpers.
 *
 * Lives next to the components so the matrix doesn't need to re-derive
 * client-side shape from the API DTO directly. The hierarchy:
 *
 *   EffectiveBranchHoursResponse  (API DTO — types/api-responses.ts)
 *               ↓ adapt
 *   MatrixState                   (this file — UI state machine)
 *               ↓ on save
 *   SetBusinessHoursBulkRequest   (API DTO again)
 *
 * Keeps the components purely declarative — they take MatrixState in and
 * dispatch reducer-style actions.
 */

import type {
    EffectiveBranchHoursEntryDto,
    EffectiveBranchHourDto,
    EffectiveBranchHoursResponse,
    HoursStaleErrorDetails,
    SetBusinessHoursBulkRequest,
    SetBusinessHoursBranchUpdate,
    SetBusinessHoursDayChange,
} from '../../types/api-responses';

/** Canonical sort order — Sun..Sat. RTL flip handled at render time only. */
export const WEEK_DAYS_ORDER: ReadonlyArray<number> = [0, 1, 2, 3, 4, 5, 6];

/** Translation key for each day's short label (column header). */
export const DAY_SHORT_KEY = (dow: number): string => {
    const map = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    return `hoursMatrix.day.${map[dow] ?? 'sun'}.short`;
};

/** Translation key for each day's full label (modal + a11y). */
export const DAY_LONG_KEY = (dow: number): string => {
    const map = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    return `hoursMatrix.day.${map[dow] ?? 'sun'}.long`;
};

/** Snapshot of one cell value as the matrix renders it. */
export interface CellValue {
    isOpen: boolean;
    openTime: string | null;        // "HH:mm" or null
    closeTime: string | null;
    is24Hours: boolean;
}

/** Per-cell client state including the OC token + diff vs the server. */
export interface CellState extends CellValue {
    dayOfWeek: number;
    /** Last-loaded server value — used to detect "dirty" + render diff. */
    serverValue: CellValue;
    /** Server-supplied OC token (epoch ms). Null = no prior audit row. */
    serverUpdatedAt: number | null;
    /** True if local edit differs from server. Drives the dot + the diff. */
    isDirty: boolean;
    /** True when a 409 marked this cell as conflicted. */
    isStale: boolean;
}

/** Branch row in the matrix — derived from API response. */
export interface BranchState {
    branchId: number;
    branchName: string;
    isPrimary: boolean;
    cells: CellState[];                  // 7 cells, sorted dayOfWeek 0..6
}

/** Matrix-level state. */
export type DivergenceMode = 'single' | 'uniform' | 'divergent';

export interface MatrixState {
    businessId: number;
    branches: BranchState[];
    defaultRow: CellValue[];             // 0..7 entries (may have gaps)
    divergence: DivergenceMode;
}

/** Comparison helper — used for matches-default + dirty checks. */
export function cellValuesEqual(a: CellValue, b: CellValue): boolean {
    if (a.isOpen !== b.isOpen) return false;
    if (a.is24Hours !== b.is24Hours) return false;
    if (!a.isOpen) return true;          // closed: ignore time fields
    return a.openTime === b.openTime && a.closeTime === b.closeTime;
}

/** Format a cell's hours as "HH:mm – HH:mm" or fall back to a "closed" sentinel. */
export function formatCellTime(cell: CellValue): { kind: 'open' | 'closed'; text: string } {
    if (!cell.isOpen) return { kind: 'closed', text: '' };
    if (cell.is24Hours) return { kind: 'open', text: '24h' };
    const open = cell.openTime ?? '00:00';
    const close = cell.closeTime ?? '23:59';
    return { kind: 'open', text: `${open} – ${close}` };
}

/* ── Adapt API → matrix state ───────────────────────────────────────── */

function dayHoursValue(h: EffectiveBranchHourDto): CellValue {
    return {
        isOpen: h.isOpen,
        openTime: h.isOpen ? h.openTime : null,
        closeTime: h.isOpen ? h.closeTime : null,
        is24Hours: h.is24Hours,
    };
}

function buildBranchCells(entry: EffectiveBranchHoursEntryDto): CellState[] {
    // Build a 0..6 cell array even if BE omits a day (treat as Closed default).
    return WEEK_DAYS_ORDER.map((dow) => {
        const h = entry.hours.find((x) => x.dayOfWeek === dow);
        const value: CellValue = h
            ? dayHoursValue(h)
            : { isOpen: false, openTime: null, closeTime: null, is24Hours: false };
        return {
            dayOfWeek: dow,
            ...value,
            serverValue: value,
            serverUpdatedAt: h?.updatedAt ?? null,
            isDirty: false,
            isStale: false,
        };
    });
}

function computeDivergence(branches: BranchState[]): DivergenceMode {
    if (branches.length <= 1) return 'single';
    const first = branches[0];
    const allMatch = WEEK_DAYS_ORDER.every((dow) =>
        branches.every((br) =>
            cellValuesEqual(
                br.cells[dow] as CellValue,
                first.cells[dow] as CellValue,
            ),
        ),
    );
    return allMatch ? 'uniform' : 'divergent';
}

function buildDefaultRowValues(
    response: EffectiveBranchHoursResponse,
    branches: BranchState[],
): CellValue[] {
    // Prefer server-supplied defaultRow if present; otherwise fall back to a
    // client-side computation (first branch wins on uniform, omit on divergent).
    if (response.defaultRow?.hours?.length) {
        return response.defaultRow.hours.map(dayHoursValue);
    }
    // Synthesize: for each day, return the value if every branch agrees.
    return WEEK_DAYS_ORDER.flatMap<CellValue>((dow) => {
        if (branches.length === 0) return [];
        const first = branches[0].cells[dow] as CellValue;
        const allMatch = branches.every((br) =>
            cellValuesEqual(br.cells[dow] as CellValue, first),
        );
        return allMatch ? [{ ...first }] : [];
    });
}

export function adaptResponseToMatrixState(
    response: EffectiveBranchHoursResponse,
): MatrixState {
    const branches: BranchState[] = (response.branches ?? []).map((entry) => ({
        branchId: entry.branchId,
        branchName: entry.branchName,
        isPrimary: entry.isPrimary,
        cells: buildBranchCells(entry),
    }));
    const divergence = computeDivergence(branches);
    return {
        businessId: response.businessId,
        branches,
        defaultRow: buildDefaultRowValues(response, branches),
        divergence,
    };
}

/* ── Default-row helpers ────────────────────────────────────────────── */

/**
 * "Effective" Default-row cell for a given day. For uniform/single matrices
 * the BE-supplied defaultRow.hours[dow] exists; for divergent days the BE
 * omits that day and the UI infers a synthetic value (the most-common value
 * across branches, with the "X differs" chip rendered alongside).
 */
export function getDefaultCellForDay(
    state: MatrixState,
    dow: number,
): { value: CellValue | null; isSynthetic: boolean; differsCount: number } {
    if (state.branches.length === 0) {
        return { value: null, isSynthetic: false, differsCount: 0 };
    }
    // Count distinct cell values across branches for this day.
    const valuesByKey = new Map<string, { value: CellValue; count: number }>();
    state.branches.forEach((br) => {
        const cell = br.cells[dow];
        if (!cell) return;
        const key = JSON.stringify({
            isOpen: cell.isOpen,
            openTime: cell.openTime,
            closeTime: cell.closeTime,
            is24Hours: cell.is24Hours,
        });
        const existing = valuesByKey.get(key);
        if (existing) existing.count += 1;
        else valuesByKey.set(key, { value: cell as CellValue, count: 1 });
    });
    if (valuesByKey.size === 0) return { value: null, isSynthetic: false, differsCount: 0 };
    if (valuesByKey.size === 1) {
        const only = Array.from(valuesByKey.values())[0];
        return { value: only.value, isSynthetic: false, differsCount: 0 };
    }
    // Most-common value wins; mark divergent.
    const sorted = Array.from(valuesByKey.values()).sort(
        (a, b) => b.count - a.count,
    );
    const majority = sorted[0];
    const divergeCount = state.branches.length - majority.count;
    return { value: majority.value, isSynthetic: true, differsCount: divergeCount };
}

/* ── Mutations: edit / revert / dirty-tracking ──────────────────────── */

export function updateBranchCell(
    state: MatrixState,
    branchId: number,
    dow: number,
    next: CellValue,
): MatrixState {
    return {
        ...state,
        branches: state.branches.map((br) => {
            if (br.branchId !== branchId) return br;
            return {
                ...br,
                cells: br.cells.map((cell) => {
                    if (cell.dayOfWeek !== dow) return cell;
                    return {
                        ...cell,
                        ...next,
                        isDirty: !cellValuesEqual(next, cell.serverValue),
                        isStale: false,
                    };
                }),
            };
        }),
    };
}

export function setBranchCellsToDefault(
    state: MatrixState,
    branchId: number,
    dow: number,
): MatrixState {
    const defaultValue = state.defaultRow.find((_, idx) => idx === dow);
    if (!defaultValue) return state;
    return updateBranchCell(state, branchId, dow, defaultValue);
}

export function dirtyCells(state: MatrixState): Array<{ branchId: number; dow: number }> {
    const out: Array<{ branchId: number; dow: number }> = [];
    state.branches.forEach((br) => {
        br.cells.forEach((cell) => {
            if (cell.isDirty) out.push({ branchId: br.branchId, dow: cell.dayOfWeek });
        });
    });
    return out;
}

export function hasDirtyCells(state: MatrixState): boolean {
    return state.branches.some((br) => br.cells.some((c) => c.isDirty));
}

export function dirtyBranchIds(state: MatrixState): number[] {
    return state.branches
        .filter((br) => br.cells.some((c) => c.isDirty))
        .map((br) => br.branchId);
}

/** Mark stale cells per the 409 Hours.Stale envelope. */
export function markStaleFromConflict(
    state: MatrixState,
    details: HoursStaleErrorDetails,
): MatrixState {
    const conflictSet = new Set(
        (details.conflictedCells ?? []).map((c) => `${c.branchId}:${c.dayOfWeek}`),
    );
    return {
        ...state,
        branches: state.branches.map((br) => ({
            ...br,
            cells: br.cells.map((cell) => ({
                ...cell,
                isStale: conflictSet.has(`${br.branchId}:${cell.dayOfWeek}`),
            })),
        })),
    };
}

/** Wipe dirty state — used after a successful save or "discard draft". */
export function discardDraft(state: MatrixState): MatrixState {
    return {
        ...state,
        branches: state.branches.map((br) => ({
            ...br,
            cells: br.cells.map((cell) => ({
                ...cell,
                isOpen: cell.serverValue.isOpen,
                openTime: cell.serverValue.openTime,
                closeTime: cell.serverValue.closeTime,
                is24Hours: cell.serverValue.is24Hours,
                isDirty: false,
                isStale: false,
            })),
        })),
    };
}

/* ── Build a PUT request from dirty cells ───────────────────────────── */

function dayChangeFromCell(cell: CellState): SetBusinessHoursDayChange {
    return {
        dayOfWeek: cell.dayOfWeek,
        isOpen: cell.isOpen,
        openTime: cell.isOpen ? cell.openTime : null,
        closeTime: cell.isOpen ? cell.closeTime : null,
        is24Hours: cell.is24Hours,
        expectedUpdatedAt: cell.serverUpdatedAt,
    };
}

/**
 * Build a bulk-write request scoped to ONE branch's edits — used by the
 * inline cell save (per-branch row in §2.5) where the bulk-modal never
 * appears.
 */
export function buildBulkRequestForBranch(
    state: MatrixState,
    branchId: number,
): SetBusinessHoursBulkRequest {
    const branch = state.branches.find((b) => b.branchId === branchId);
    if (!branch) return { branches: [] };
    const days = branch.cells
        .filter((c) => c.isDirty)
        .map(dayChangeFromCell);
    return {
        branches: [{ branchId, days }],
    };
}

/**
 * Build a bulk-write request for the Default-row edit flow. The caller
 * supplies the SET of branchIds the owner ticked, and a SET of dayOfWeek
 * values the owner edited (the dirty days on the Default row only). The
 * day values themselves come from the matrix's current local state.
 *
 * Per Faris §3.2 the per-day token comes from each branch's own cell, not
 * the Default row — the audit's optimistic-concurrency check is per-cell.
 */
export function buildBulkRequestForBulkApply(
    state: MatrixState,
    selectedBranchIds: ReadonlySet<number>,
    editedDows: ReadonlySet<number>,
    dayValueOverride: Map<number, CellValue>,
): SetBusinessHoursBulkRequest {
    const branchUpdates: SetBusinessHoursBranchUpdate[] = state.branches
        .filter((br) => selectedBranchIds.has(br.branchId))
        .map((br) => {
            const days: SetBusinessHoursDayChange[] = [];
            br.cells.forEach((cell) => {
                if (!editedDows.has(cell.dayOfWeek)) return;
                const overrideValue = dayValueOverride.get(cell.dayOfWeek);
                if (!overrideValue) return;
                days.push({
                    dayOfWeek: cell.dayOfWeek,
                    isOpen: overrideValue.isOpen,
                    openTime: overrideValue.isOpen ? overrideValue.openTime : null,
                    closeTime: overrideValue.isOpen ? overrideValue.closeTime : null,
                    is24Hours: overrideValue.is24Hours,
                    expectedUpdatedAt: cell.serverUpdatedAt,
                });
            });
            return { branchId: br.branchId, days };
        })
        .filter((bu) => bu.days.length > 0);
    return { branches: branchUpdates };
}

/* ── Validation: time format + ordering ─────────────────────────────── */

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

export interface CellValidationResult {
    ok: boolean;
    error?: string;
}

export function validateCellValue(value: CellValue): CellValidationResult {
    if (!value.isOpen) return { ok: true };
    if (value.is24Hours) return { ok: true };
    if (!value.openTime || !value.closeTime) {
        return { ok: false, error: 'Open and close times required when open' };
    }
    if (!TIME_RE.test(value.openTime) || !TIME_RE.test(value.closeTime)) {
        return { ok: false, error: 'Times must be HH:mm' };
    }
    if (value.openTime >= value.closeTime) {
        return { ok: false, error: 'Open must be before close' };
    }
    return { ok: true };
}
